import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  Paper, 
  Button, 
  Stepper, 
  Step, 
  StepLabel,
  StepButton,
  Alert,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  SelectChangeEvent,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stack
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import WavingHandIcon from '@mui/icons-material/WavingHand';
import HistoryIcon from '@mui/icons-material/History';
import CategoryIcon from '@mui/icons-material/Category';
import RecommendIcon from '@mui/icons-material/Recommend';
import CelebrationIcon from '@mui/icons-material/Celebration';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import WarningIcon from '@mui/icons-material/Warning';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import StorageIcon from '@mui/icons-material/Storage';
import BusinessIcon from '@mui/icons-material/Business';
import InventoryIcon from '@mui/icons-material/Inventory';
import InfoIcon from '@mui/icons-material/Info';
import VpnKeyIcon from '@mui/icons-material/VpnKey';

// Components
import FileUploader from '../components/FileUpload/FileUploader';

// Context
import { useFileData } from '../context/FileDataContext';

// Utils
import { parseExcelFile } from '../utils/fileParser';
import { uploadFileAndNotifyApi } from '../services/s3Service';

// Define missing types
interface ModelOption {
  id: string;
  name: string;
  description: string;
}

interface ProcessOption {
  id: string;
  name: string;
  description: string;
}

const steps = ['Upload File', 'Preview Data', 'Process Data', 'March 2025 spend 🎉'];

// Mock data for previously uploaded files
const previousUploads = [
  { id: 1, name: 'Q1_2023_Procurement.xlsx', date: '2023-03-31', size: '2.4 MB' },
  { id: 2, name: 'Q2_2023_Procurement.xlsx', date: '2023-06-30', size: '2.7 MB' },
  { id: 3, name: 'Vendor_Analysis_2023.xlsx', date: '2023-07-15', size: '1.8 MB' },
  { id: 4, name: 'IT_Spending_2023.csv', date: '2023-08-01', size: '1.2 MB' },
  { id: 5, name: 'spend_cube_test.csv', date: '2024-02-28', size: '12 KB' },
];

// Taxonomy options
const taxonomyOptions = [
  { id: 'general', name: 'General', description: 'Basic classification for all types of spend' },
  { id: 'unspsc', name: 'UNSPSC', description: 'United Nations Standard Products and Services Code', recommended: true },
  { id: 'it', name: 'IT Specific', description: 'Specialized for IT and technology purchases' },
  { id: 'healthcare', name: 'Healthcare', description: 'Medical and healthcare specific classifications' },
  { id: 'manufacturing', name: 'Manufacturing', description: 'Industrial and manufacturing focused' },
];

// Find the recommended taxonomy option
const recommendedTaxonomy = taxonomyOptions.find(taxonomy => taxonomy.recommended)?.id || 'general';

const FileUploadPage: React.FC = () => {
  const navigate = useNavigate();
  const { 
    fileData, 
    setFile, 
    setData, 
    startProcessing, 
    setS3UploadSuccess, 
    setS3UploadError, 
    setApiNotificationSuccess, 
    setApiNotificationError,
    setSelectedColumns
  } = useFileData();
  const [activeStep, setActiveStep] = useState(0);
  const [completed, setCompleted] = useState<{ [k: number]: boolean }>({});
  const [error, setError] = useState<string | null>(null);
  const [selectedPreviousFile, setSelectedPreviousFile] = useState<string>('');
  const [selectedRefreshSchedule, setSelectedRefreshSchedule] = useState<string>('');
  const [selectedTaxonomy, setSelectedTaxonomy] = useState<string>(recommendedTaxonomy);
  const [isUploading, setIsUploading] = useState(false);
  const [fileName, setFileName] = useState('');
  const [fileSize, setFileSize] = useState(0);
  const [fileError, setFileError] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const [modelOptions, setModelOptions] = useState<ModelOption[]>([]);
  const [selectedModel, setSelectedModel] = useState('');
  const [processOptions, setProcessOptions] = useState<ProcessOption[]>([]);
  const [selectedProcess, setSelectedProcess] = useState('');
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [showProcessingModal, setShowProcessingModal] = useState(false);

  // Always reset to recommended taxonomy when changing steps
  useEffect(() => {
    if (activeStep === 1) {
      setSelectedTaxonomy(recommendedTaxonomy);
    }
  }, [activeStep]);

  const handleFileUpload = async (file: File) => {
    try {
      setError(null);
      setFile(file);
      setIsUploading(true);
      
      // Upload file to S3 and notify API
      const { s3Upload, apiNotification } = await uploadFileAndNotifyApi(file, {
        taxonomy: selectedTaxonomy,
        uploadedBy: 'user', // This could be dynamic based on user authentication
        uploadedAt: new Date().toISOString()
      });
      
      // Handle S3 upload result
      if (s3Upload.success && s3Upload.location && s3Upload.key) {
        setS3UploadSuccess(s3Upload.location, s3Upload.key);
      } else {
        setS3UploadError(s3Upload.error || 'Unknown error during S3 upload');
        setError(`Error uploading to cloud storage: ${s3Upload.error}`);
        setIsUploading(false);
        return;
      }
      
      // Handle API notification result
      if (apiNotification) {
        if (apiNotification.success) {
          setApiNotificationSuccess(apiNotification.response);
        } else {
          setApiNotificationError(apiNotification.error || 'Unknown error during API notification');
          // We continue even if API notification fails, but log the error
          console.error('API notification failed:', apiNotification.error);
        }
      }
      
      // Parse the file for local processing
      const parsedData = await parseExcelFile(file);
      setData(parsedData);
      
      // Mark step as completed and move to next step
      handleComplete(0);
      setActiveStep(1);
      setIsUploading(false);
    } catch (err) {
      setError('Error processing file. Please try again.');
      console.error(err);
      setIsUploading(false);
    }
  };

  const handleProceed = () => {
    if (activeStep === 1) {
      // Move to the processing step
      handleComplete(1);
      setActiveStep(2); // Go to processing step
    } else if (activeStep === 2) {
      // Show processing modal and start processing
      setShowProcessingModal(true);
      setIsProcessing(true);
      
      // Simulate processing delay
      setTimeout(() => {
        // Complete processing step and go to analysis
        handleComplete(2);
        setIsProcessing(false);
        setShowProcessingModal(false);
        navigate('/analysis');
      }, 3000); // 3 second delay to simulate processing
    }
  };

  const handlePreviousFileSelect = (event: SelectChangeEvent) => {
    const value = event.target.value;
    setSelectedPreviousFile(value);
    
    if (value) {
      // Find the selected file from our mock data
      const selectedFile = previousUploads.find(file => file.id.toString() === value);
      
      if (selectedFile) {
        // Clear any previous errors
        setError(null);
        
        // Special handling for spend_cube_test.csv - load the actual file
        if (selectedFile.name === 'spend_cube_test.csv') {
          // Show loading state
          setIsUploading(true);
          
          // Fetch the actual CSV file from public/data directory
          fetch('/data/spend_cube_test.csv')
            .then(response => {
              if (!response.ok) {
                throw new Error(`Failed to fetch file: ${response.status} ${response.statusText}`);
              }
              return response.text(); // Get as text instead of blob for direct parsing
            })
            .then(csvText => {
              console.log('CSV file fetched successfully, length:', csvText.length);
              
              try {
                // Direct CSV parsing
                const lines = csvText.split('\n');
                
                if (lines.length > 1) {
                  // Get headers from first line - only use the most important columns
                  const allHeaders = lines[0].split(',').map(h => h.trim());
                  
                  // Use all headers instead of filtering to important ones
                  const usedHeaders = allHeaders;
                  
                  console.log('Using all headers:', usedHeaders);
                  
                  // Parse data rows (limit to 20 for preview)
                  const parsedRows = lines.slice(1, 21)
                    .filter(line => line.trim() !== '')
                    .map((line, rowIndex) => {
                      // Split the CSV line properly, handling quoted values
                      const values = parseCSVLine(line);
                      const row: Record<string, any> = { id: rowIndex + 1 };
                      
                      // Include all columns
                      usedHeaders.forEach((header, i) => {
                        if (header && header !== 'Unnamed: 20' && !header.startsWith('Unnamed:')) {
                          row[header] = values[i] || '';
                        }
                      });
                      
                      return row;
                    });
                  
                  console.log('Parsed CSV data:', parsedRows);
                  
                  // Create a File object for the context
                  const blob = new Blob([csvText], { type: 'text/csv' });
                  const csvFile = new File([blob], selectedFile.name, { type: 'text/csv' });
                  setFile(csvFile);
                  
                  // Set the parsed data
                  setData(parsedRows);
                  
                  // Mark step as completed and move to next step
                  handleComplete(0);
                  setActiveStep(1);
                } else {
                  throw new Error('CSV file has insufficient data');
                }
              } catch (error) {
                console.error('Error parsing CSV file:', error);
                setError(`Error parsing ${selectedFile.name}: ${error instanceof Error ? error.message : 'Unknown error'}`);
                
                // Fallback to mock data
                fallbackToMockData(selectedFile.name);
              }
              
              setIsUploading(false);
            })
            .catch(err => {
              console.error('Error loading CSV file:', err);
              setError(`Error loading ${selectedFile.name}: ${err.message}`);
              setIsUploading(false);
              
              // Fallback to mock data
              fallbackToMockData(selectedFile.name);
            });
        } else {
          // For other files, use the existing mock data approach
          // Simulate loading
          setTimeout(() => {
            fallbackToMockData(selectedFile.name);
          }, 1000);
        }
      }
    }
  };
  
  // Helper function to parse CSV line correctly, handling quoted values
  const parseCSVLine = (line: string): string[] => {
    const result: string[] = [];
    let current = '';
    let inQuotes = false;
    
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      
      if (char === '"') {
        // Toggle quote state
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        // End of field
        result.push(current);
        current = '';
      } else {
        // Add character to current field
        current += char;
      }
    }
    
    // Add the last field
    result.push(current);
    
    return result;
  };
  
  // Helper function to create mock data when needed
  const fallbackToMockData = (fileName: string) => {
    console.log('Using mock data for', fileName);
    
    // Create mock data
    const mockData = Array(20).fill(0).map((_, index) => ({
      id: index + 1,
      'BU': `BU${(index % 3) + 1}`,
      'Department code': `DEPT${String(index % 6).padStart(3, '0')}`,
      'Order number': `ORD-${String(index + 1).padStart(3, '0')}`,
      'Related order number': `REL-${String(index + 1).padStart(3, '0')}`,
      'Created date': new Date(2023, Math.floor(index / 3), (index % 28) + 1).toISOString().split('T')[0],
      '1st Approval Date': new Date(2023, Math.floor(index / 3), (index % 28) + 3).toISOString().split('T')[0],
      'Order date': new Date(2023, Math.floor(index / 3), (index % 28) + 5).toISOString().split('T')[0],
      'Cost category': ['IT Equipment', 'Office Supplies', 'Professional Services', 'Travel', 'Marketing'][index % 5],
      'Contract': `CONT-${String(index % 8 + 1).padStart(3, '0')}`,
      'Contract status': ['Active', 'Pending', 'Completed'][index % 3],
      'Project name': `Project ${String.fromCharCode(65 + (index % 26))}`,
      'Supplier ID': `SUP-${String(index % 5 + 1).padStart(3, '0')}`,
      'Supplier Name': ['Acme Corp', 'Office World', 'Consulting Partners', 'Marketing Solutions', 'Travel Agency'][index % 5],
      'Order amount': Math.round(Math.random() * 100000) / 100
    }));
    
    // Update the context with the "loaded" file and data
    const mockFile = new File([""], fileName, { type: "text/csv" });
    setFile(mockFile);
    setData(mockData);
    
    // Mark upload step as completed and go to preview step
    handleComplete(0); // Upload completed
    setActiveStep(1); // Go to preview step
    setIsUploading(false);
  };

  const handleTaxonomyChange = (event: SelectChangeEvent) => {
    setSelectedTaxonomy(event.target.value);
    // In a real application, this would trigger a reclassification of the data
    // based on the selected taxonomy
  };

  // Handle step completion
  const handleComplete = (step: number) => {
    const newCompleted = { ...completed };
    newCompleted[step] = true;
    setCompleted(newCompleted);
  };

  // Handle step click
  const handleStep = (step: number) => () => {
    // Only allow navigation to steps that have data or are completed
    if (step === 0 || (step === 1 && fileData.file) || completed[step - 1]) {
      setActiveStep(step);
    }
  };

  // Check if a step is completed
  const isStepComplete = (step: number) => {
    return completed[step];
  };

  // Check if a step can be selected
  const canSelectStep = (step: number) => {
    // Always allow going to upload step
    if (step === 0) return true;
    
    // Allow going to preview step only if file is uploaded
    if (step === 1) return !!fileData.file;
    
    // Allow going to processing step only if preview step is completed
    if (step === 2) return completed[1];
    
    // Allow going to analysis step only if processing step is completed
    if (step === 3) return completed[2];
    
    return false;
  };

  const handlePreviewData = () => {
    // Only navigate to data preview if file is uploaded
    if (fileData.file) {
      setActiveStep(1);
    }
  };

  const handleRefreshScheduleChange = (event: SelectChangeEvent) => {
    const value = event.target.value;
    setSelectedRefreshSchedule(value);
    
    if (value) {
      // Clear any previous errors
      setError(null);
      setIsUploading(true);
      
      // Create appropriate file name based on selection
      const fileName = value === 'weekly' 
        ? 'This_weeks_Purchase_orders.csv' 
        : 'This_months_Purchase_orders.csv';
      
      // Create mock data for the selected extract
      setTimeout(() => {
        // Australian products for Kangaroo Commodities Group
        const australianProducts = [
          'Premium Australian Wheat', 
          'Merino Wool Bales', 
          'Iron Ore Grade A', 
          'Macadamia Nuts (Bulk)', 
          'Kangaroo Leather Hides', 
          'Eucalyptus Timber', 
          'Lithium Ore',
          'Australian Barley',
          'Murray River Salt',
          'Western Red Kangaroo Meat',
          'Queensland Coal',
          'Tasmanian Lavender',
          'Finger Lime Extract',
          'Jarrah Hardwood'
        ];
        
        // Standard office products for other suppliers
        const standardProducts = [
          'Laptop Computer', 
          'Office Chair', 
          'Cloud Storage Subscription', 
          'Consulting Services', 
          'Office Supplies'
        ];
        
        // Supplier list with Kangaroo Commodities Group appearing more frequently (Pareto principle)
        const suppliers = [
          'Kangaroo Commodities Group',
          'Kangaroo Commodities Group',
          'Kangaroo Commodities Group',
          'Kangaroo Commodities Group',
          'Acme Corp',
          'Kangaroo Commodities Group',
          'Kangaroo Commodities Group',
          'Tech Solutions',
          'Kangaroo Commodities Group',
          'Office Supplies Inc.',
          'Kangaroo Commodities Group',
          'Global Services'
        ];

        // Create mock data with 961 rows and Kangaroo Commodities Group as the biggest supplier
        const mockData = Array(961).fill(0).map((_, index) => {
          // Determine supplier (using the weighted list to make Kangaroo appear more frequently)
          const supplierName = suppliers[index % suppliers.length];
          
          // Make Kangaroo Commodities Group have higher spend amounts (3-10x more)
          const baseAmount = Math.random() * 5000 + 1000; // Base amount between $1,000 and $6,000
          const orderAmount = supplierName === 'Kangaroo Commodities Group'
            ? baseAmount * (Math.random() * 7 + 3) // 3-10x multiplier for Kangaroo
            : baseAmount;
            
          // Select appropriate product based on supplier
          const productDescription = supplierName === 'Kangaroo Commodities Group' 
            ? australianProducts[index % australianProducts.length]
            : standardProducts[index % standardProducts.length];
            
          return {
            id: index + 1,
            'BU': `BU${(index % 3) + 1}`,
            'Department code': `DEPT${String(index % 12).padStart(3, '0')}`,
            'Order number': `ORD-${String(index + 1).padStart(5, '0')}`,
            'Related order number': `REL-${String(index + 1).padStart(5, '0')}`,
            'Created date': new Date(2023, Math.floor(index / 80) % 12, (index % 28) + 1).toISOString().split('T')[0],
            '1st Approval Date': new Date(2023, Math.floor(index / 80) % 12, (index % 28) + 3).toISOString().split('T')[0],
            'Order date': new Date(2023, Math.floor(index / 80) % 12, (index % 28) + 5).toISOString().split('T')[0],
            'Cost category': ['Raw Materials', 'Agricultural Goods', 'Mining Resources', 'Specialty Products', 'Energy Resources'][index % 5],
            'Contract': `CONT-${String(index % 50 + 1).padStart(3, '0')}`,
            'Contract status': ['Active', 'Pending', 'Completed'][index % 3],
            'Project name': `Project ${String.fromCharCode(65 + (index % 26))}`,
            'Supplier ID': `SUP-${String((suppliers.indexOf(supplierName) % 5) + 1).padStart(3, '0')}`,
            'Supplier Name': supplierName,
            'Description': productDescription,
            'Order amount': Math.round(orderAmount * 100) / 100
          };
        });
        
        // Log total records and spend distribution
        console.log(`Generated ${mockData.length} mock records`);
        
        // Calculate spend distribution
        const spendBySupplier = mockData.reduce((acc, row) => {
          const supplier = row['Supplier Name'];
          const amount = typeof row['Order amount'] === 'string' 
            ? parseFloat(row['Order amount']) 
            : row['Order amount'];
          acc[supplier] = (acc[supplier] || 0) + amount;
          return acc;
        }, {} as Record<string, number>);
        
        const totalSpend = Object.values(spendBySupplier).reduce((sum, amount) => sum + amount, 0);
        
        Object.entries(spendBySupplier).forEach(([supplier, amount]) => {
          const percentage = (amount / totalSpend * 100).toFixed(2);
          console.log(`${supplier}: $${amount.toFixed(2)} (${percentage}%)`);
        });
        
        // Update the context with the "loaded" file and data
        const mockFile = new File([""], fileName, { type: "text/csv" });
        setFile(mockFile);
        setData(mockData);
        
        // Mark upload step as completed and go to preview step
        handleComplete(0); // Upload completed
        setActiveStep(1); // Go to preview step
        setIsUploading(false);
      }, 800); // Short delay to simulate loading
    }
  };

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Welcome to March 2025 spend <WavingHandIcon sx={{ color: '#e84118', verticalAlign: 'middle', ml: 1 }} />
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Follow these steps to get started and analyse your procurement data.
        </Typography>
      </Box>
      
      <Card 
        elevation={0} 
        sx={{ 
          p: 3, 
          mb: 4,
          bgcolor: 'rgba(142, 44, 76, 0.05)', 
          border: '1px solid rgba(142, 44, 76, 0.1)',
          borderRadius: 3
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 500 }}>
            Note: The system will automatically classify your items based on industry-standard taxonomies. 
            You will have the opportunity to review and adjust classifications in the next step.
          </Typography>
        </Box>
      </Card>
      
      <Stepper 
        activeStep={activeStep} 
        nonLinear
        sx={{ 
          mb: 4, 
          '& .MuiStepLabel-root .Mui-completed': {
            color: 'primary.main', 
          },
          '& .MuiStepLabel-root .Mui-active': {
            color: 'primary.main', 
          },
        }}
      >
        {steps.map((label, index) => (
          <Step key={label} completed={isStepComplete(index)}>
            <StepButton 
              onClick={handleStep(index)} 
              disabled={!canSelectStep(index)}
              sx={{
                cursor: canSelectStep(index) ? 'pointer' : 'not-allowed',
                opacity: canSelectStep(index) ? 1 : 0.6,
                '&:hover': {
                  backgroundColor: canSelectStep(index) ? 'rgba(142, 44, 76, 0.04)' : 'transparent',
                },
                '& .MuiStepLabel-label': {
                  color: canSelectStep(index) ? 'text.primary' : 'text.disabled',
                }
              }}
            >
              {label}
            </StepButton>
          </Step>
        ))}
      </Stepper>
      
      {error && (
        <Alert 
          severity="error" 
          sx={{ 
            mb: 2, 
            borderRadius: 2,
            '& .MuiAlert-icon': {
              color: 'error.main'
            }
          }}
        >
          {error}
        </Alert>
      )}
      
      <Card 
        elevation={0} 
        sx={{ 
          mb: 3, 
          border: '1px solid #e0e0e0',
          borderRadius: 3,
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box'
        }}
      >
        <CardContent sx={{ 
          p: 4, 
          overflow: 'visible',
          display: 'flex', 
          flexDirection: 'column',
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box'
        }}>
          {activeStep === 0 ? (
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 0 }}>
                  Get to know March 2025 spend
                </Typography>
                {fileData.file && (
                  <Button 
                    variant="contained" 
                    color="secondary" 
                    size="medium"
                    startIcon={<VisibilityIcon />}
                    onClick={handlePreviewData}
                    sx={{ 
                      px: 2, 
                      py: 1,
                      fontWeight: 500
                    }}
                  >
                    Preview Data
                  </Button>
                )}
              </Box>
              <Typography variant="body1" paragraph sx={{ color: 'text.secondary', mb: 3 }}>
                Please upload your procurement data file (Excel or CSV format). 
                The file should contain columns for item descriptions, vendors, costs, and quantities.
              </Typography>
              
              {/* Scheduled Extracts dropdown */}
              <Box sx={{ mb: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <HistoryIcon sx={{ color: 'primary.main', mr: 1 }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                    Scheduled Extracts
                  </Typography>
                </Box>
                <FormControl fullWidth variant="outlined" size="small" sx={{ mb: 2 }}>
                  <InputLabel id="refresh-schedule-label">Select extract type</InputLabel>
                  <Select
                    labelId="refresh-schedule-label"
                    id="refresh-schedule-select"
                    value={selectedRefreshSchedule}
                    onChange={handleRefreshScheduleChange}
                    label="Select extract type"
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    <MenuItem value="weekly">This weeks Purchase orders</MenuItem>
                    <MenuItem value="monthly">This months Purchase orders</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              
              {/* Previously uploaded files dropdown */}
              <Box sx={{ mb: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <AutoAwesomeIcon sx={{ color: 'primary.main', mr: 1 }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                    Previously Uploaded Files
                  </Typography>
                </Box>
                <FormControl fullWidth variant="outlined" size="small" sx={{ mb: 2 }}>
                  <InputLabel id="previous-files-label">Select a previously uploaded file</InputLabel>
                  <Select
                    labelId="previous-files-label"
                    id="previous-files-select"
                    value={selectedPreviousFile}
                    onChange={handlePreviousFileSelect}
                    label="Select a previously uploaded file"
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {previousUploads.map((file) => (
                      <MenuItem key={file.id} value={file.id.toString()}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                          <Typography variant="body2">{file.name}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {file.date} • {file.size}
                          </Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                
                <Typography variant="body2" color="text.secondary">
                  Or upload a new file below
                </Typography>
              </Box>
              
              <Divider sx={{ my: 3 }} />
              
              <FileUploader 
                onFileUpload={handleFileUpload} 
                isUploading={isUploading}
                s3UploadStatus={{
                  isUploaded: fileData.s3Upload.isUploaded,
                  error: fileData.s3Upload.error
                }}
                apiNotificationStatus={{
                  isNotified: fileData.apiNotification.isNotified,
                  error: fileData.apiNotification.error
                }}
              />
            </Box>
          ) : activeStep === 1 ? (
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 0 }}>
                  Data Preview
                </Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button 
                    variant="outlined" 
                    color="primary" 
                    size="medium"
                    startIcon={<ArrowBackIcon />}
                    onClick={() => setActiveStep(0)}
                  >
                    Back to Upload
                  </Button>
                  <Button 
                    variant="contained" 
                    color="secondary" 
                    size="medium"
                    endIcon={<ArrowForwardIcon />}
                    onClick={handleProceed}
                    sx={{ 
                      px: 2, 
                      py: 1,
                      fontWeight: 500
                    }}
                  >
                    Process Data
                  </Button>
                </Box>
              </Box>
              <Typography variant="body1" paragraph sx={{ color: 'text.secondary', mb: 3 }}>
                Review your data before proceeding to processing. The system will automatically analyze your uploaded file.
              </Typography>
              
              {/* Taxonomy selection dropdown */}
              <Box sx={{ mb: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <CategoryIcon sx={{ color: 'primary.main', mr: 1 }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                    Classification Taxonomy
                  </Typography>
                </Box>
                <FormControl fullWidth variant="outlined" size="small" sx={{ mb: 2 }}>
                  <InputLabel id="taxonomy-label">Select classification taxonomy</InputLabel>
                  <Select
                    labelId="taxonomy-label"
                    id="taxonomy-select"
                    value={selectedTaxonomy}
                    onChange={handleTaxonomyChange}
                    label="Select classification taxonomy"
                  >
                    {taxonomyOptions.map((taxonomy) => (
                      <MenuItem key={taxonomy.id} value={taxonomy.id}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                          <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'space-between',
                            mb: 0.5
                          }}>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>{taxonomy.name}</Typography>
                            {taxonomy.recommended && (
                              <Chip
                                icon={<RecommendIcon fontSize="small" />}
                                label="Recommended"
                                size="small"
                                color="secondary"
                                sx={{ 
                                  height: 22, 
                                  transform: 'translateY(-1px)',
                                  '& .MuiChip-label': { px: 1, py: 0, fontSize: '0.7rem', fontWeight: 500 },
                                  '& .MuiChip-icon': { ml: 0.5, fontSize: '0.9rem' }
                                }}
                              />
                            )}
                          </Box>
                          <Typography variant="caption" color="text.secondary">
                            {taxonomy.description}
                          </Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Typography variant="body2" color="text.secondary">
                  Select a taxonomy to determine how your spend data will be classified
                </Typography>
              </Box>
              
              {fileData.data && (
                <Box sx={{ width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
                  <Alert severity="success" sx={{ mb: 2 }}>
                    <Typography variant="body1">
                      File successfully uploaded and parsed. {fileData.data.length} rows ready for processing.
                    </Typography>
                  </Alert>
                  
                  {/* AI Detected Columns Section */}
                  <Box sx={{ mt: 4, mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <AutoAwesomeIcon sx={{ color: 'primary.main', mr: 1 }} />
                      <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                        AI Detected Columns
                      </Typography>
                    </Box>
                    
                    <Typography variant="body2" color="text.secondary" mb={3}>
                      Our AI has identified key data columns for your spend analysis:
                    </Typography>
                    
                    <Stack spacing={2}>
                      {/* Supplier Name Column */}
                      <Card variant="outlined" sx={{ mb: 2 }}>
                        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <BusinessIcon sx={{ color: 'primary.main', mr: 1 }} />
                            <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                              Supplier Name
                            </Typography>
                          </Box>
                          <Box sx={{ pl: 4 }}>
                            <Chip 
                              label="Supplier Name" 
                              color="secondary" 
                              size="small" 
                              sx={{ fontWeight: 500 }}
                            />
                            <Typography variant="caption" display="block" mt={1} color="text.secondary">
                              This column contains your vendor/supplier information
                            </Typography>
                          </Box>
                        </CardContent>
                      </Card>
                      
                      {/* Item Name Column */}
                      <Card variant="outlined" sx={{ mb: 2 }}>
                        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <InventoryIcon sx={{ color: 'primary.main', mr: 1 }} />
                            <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                              Item Name
                            </Typography>
                          </Box>
                          <Box sx={{ pl: 4 }}>
                            <Chip 
                              label="Description" 
                              color="secondary" 
                              size="small" 
                              sx={{ fontWeight: 500 }}
                            />
                            <Typography variant="caption" display="block" mt={1} color="text.secondary">
                              This column contains your purchased item descriptions
                            </Typography>
                          </Box>
                        </CardContent>
                      </Card>
                      
                      {/* IDs Column */}
                      <Card variant="outlined" sx={{ mb: 2 }}>
                        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <VpnKeyIcon sx={{ color: 'primary.main', mr: 1 }} />
                            <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                              IDs
                            </Typography>
                          </Box>
                          <Box sx={{ pl: 4, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            <Chip 
                              label="Supplier ID" 
                              color="secondary" 
                              size="small" 
                              sx={{ fontWeight: 500 }}
                            />
                            <Chip 
                              label="Item ID" 
                              color="secondary" 
                              size="small" 
                              sx={{ fontWeight: 500 }}
                            />
                            <Typography variant="caption" display="block" width="100%" mt={1} color="text.secondary">
                              These columns contain unique identifiers for suppliers and items
                            </Typography>
                          </Box>
                        </CardContent>
                      </Card>
                      
                      {/* Context Columns */}
                      <Card variant="outlined" sx={{ mb: 2 }}>
                        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <InfoIcon sx={{ color: 'primary.main', mr: 1 }} />
                            <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                              Context Columns
                            </Typography>
                          </Box>
                          <Box sx={{ pl: 4, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            <Chip label="Amount" size="small" />
                            <Chip label="Department" size="small" />
                            <Chip label="Business Unit" size="small" />
                            <Chip label="Order Date" size="small" />
                            <Typography variant="caption" display="block" width="100%" mt={1} color="text.secondary">
                              These additional columns provide important context for your analysis
                            </Typography>
                          </Box>
                        </CardContent>
                      </Card>
                    </Stack>
                  </Box>
                  
                  {/* Data Preview Table */}
                  <Box sx={{ mt: 4, mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <StorageIcon sx={{ color: 'primary.main', mr: 1 }} />
                      <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                        Data Preview
                      </Typography>
                    </Box>
                    
                    <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400, mb: 2 }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            {/* Only show AI-detected columns in specific order */}
                            {/* 1. Supplier ID */}
                            <TableCell 
                              sx={{
                                fontWeight: 'bold',
                                backgroundColor: 'secondary.light',
                                color: 'white'
                              }}
                            >
                              Supplier ID
                              <AutoAwesomeIcon sx={{ ml: 0.5, fontSize: '0.9rem', verticalAlign: 'text-top' }} />
                            </TableCell>
                            {/* 2. Supplier Name */}
                            <TableCell 
                              sx={{
                                fontWeight: 'bold',
                                backgroundColor: 'secondary.light',
                                color: 'white'
                              }}
                            >
                              Supplier Name
                              <AutoAwesomeIcon sx={{ ml: 0.5, fontSize: '0.9rem', verticalAlign: 'text-top' }} />
                            </TableCell>
                            {/* 3. Item ID */}
                            <TableCell 
                              sx={{
                                fontWeight: 'bold',
                                backgroundColor: 'secondary.light',
                                color: 'white'
                              }}
                            >
                              Item ID
                              <AutoAwesomeIcon sx={{ ml: 0.5, fontSize: '0.9rem', verticalAlign: 'text-top' }} />
                            </TableCell>
                            {/* 4. Item Name (Description) */}
                            <TableCell 
                              sx={{
                                fontWeight: 'bold',
                                backgroundColor: 'secondary.light',
                                color: 'white'
                              }}
                            >
                              Description
                              <AutoAwesomeIcon sx={{ ml: 0.5, fontSize: '0.9rem', verticalAlign: 'text-top' }} />
                            </TableCell>
                            {/* Context columns */}
                            <TableCell 
                              sx={{
                                fontWeight: 'bold',
                                backgroundColor: 'rgba(142, 44, 76, 0.08)'
                              }}
                            >
                              Amount
                            </TableCell>
                            <TableCell 
                              sx={{
                                fontWeight: 'bold',
                                backgroundColor: 'rgba(142, 44, 76, 0.08)'
                              }}
                            >
                              Department
                            </TableCell>
                            <TableCell 
                              sx={{
                                fontWeight: 'bold',
                                backgroundColor: 'rgba(142, 44, 76, 0.08)'
                              }}
                            >
                              Business Unit
                            </TableCell>
                            <TableCell 
                              sx={{
                                fontWeight: 'bold',
                                backgroundColor: 'rgba(142, 44, 76, 0.08)'
                              }}
                            >
                              Order Date
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {fileData.data.slice(0, 10).map((row, rowIndex) => (
                            <TableRow key={rowIndex}>
                              {/* 1. Supplier ID */}
                              <TableCell sx={{ backgroundColor: 'rgba(232, 65, 24, 0.05)' }}>
                                {row['Supplier ID'] || `SUP-${String(rowIndex % 5 + 100).padStart(3, '0')}`}
                              </TableCell>
                              {/* 2. Supplier Name */}
                              <TableCell sx={{ backgroundColor: 'rgba(232, 65, 24, 0.05)' }}>
                                {row['Supplier Name'] || ['Kangaroo Commodities Group', 'Acme Corp', 'Tech Solutions', 'Office Supplies Inc.', 'Global Services'][rowIndex % 5]}
                              </TableCell>
                              {/* 3. Item ID */}
                              <TableCell sx={{ backgroundColor: 'rgba(232, 65, 24, 0.05)' }}>
                                {row['Item ID'] || `ITEM-${String(rowIndex % 8 + 1000).padStart(4, '0')}`}
                              </TableCell>
                              {/* 4. Item Name (Description) */}
                              <TableCell sx={{ backgroundColor: 'rgba(232, 65, 24, 0.05)' }}>
                                {row['Description'] || (row['Supplier Name'] === 'Kangaroo Commodities Group' 
                                  ? ['Premium Australian Wheat', 'Merino Wool Bales', 'Iron Ore Grade A', 'Macadamia Nuts (Bulk)', 'Kangaroo Leather Hides', 'Eucalyptus Timber', 'Lithium Ore'][rowIndex % 7]
                                  : ['Laptop Computer', 'Office Chair', 'Cloud Storage Subscription', 'Consulting Services', 'Office Supplies'][rowIndex % 5])
                                }
                              </TableCell>
                              {/* Context columns */}
                              <TableCell sx={{ backgroundColor: 'rgba(142, 44, 76, 0.02)' }}>
                                {row['Amount'] || row['Order amount'] || `$${(Math.random() * 10000).toFixed(2)}`}
                              </TableCell>
                              <TableCell sx={{ backgroundColor: 'rgba(142, 44, 76, 0.02)' }}>
                                {row['Department'] || row['Department code'] || ['IT', 'HR', 'Finance', 'Operations', 'Marketing'][rowIndex % 5]}
                              </TableCell>
                              <TableCell sx={{ backgroundColor: 'rgba(142, 44, 76, 0.02)' }}>
                                {row['Business Unit'] || row['BU'] || `BU${(rowIndex % 3) + 1}`}
                              </TableCell>
                              <TableCell sx={{ backgroundColor: 'rgba(142, 44, 76, 0.02)' }}>
                                {row['Order Date'] || row['Order date'] || new Date(2023, (rowIndex % 12), (rowIndex % 28) + 1).toISOString().split('T')[0]}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    <Typography variant="caption" color="text.secondary">
                      Showing 10 of {fileData.data.length} total rows ({fileData.data.length === 961 ? 'complete dataset' : 'partial dataset'}). Only displaying AI-detected important columns.
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
          ) : activeStep === 2 ? (
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 0 }}>
                  Process Data
                </Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button 
                    variant="outlined" 
                    color="primary" 
                    size="medium"
                    startIcon={<ArrowBackIcon />}
                    onClick={() => setActiveStep(1)}
                  >
                    Back to Preview
                  </Button>
                  <Button 
                    variant="contained" 
                    color="secondary" 
                    size="medium"
                    endIcon={<ArrowForwardIcon />}
                    onClick={handleProceed}
                    sx={{ 
                      px: 2, 
                      py: 1,
                      fontWeight: 500
                    }}
                  >
                    Start Analysis
                  </Button>
                </Box>
              </Box>
              <Typography variant="body1" paragraph sx={{ color: 'text.secondary', mb: 3 }}>
                Choose processing options for your data. These settings determine how your data will be analyzed.
              </Typography>
              
              {/* Processing Options */}
              <Box sx={{ mb: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <ImportExportIcon sx={{ color: 'primary.main', mr: 1 }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                    Processing Options
                  </Typography>
                </Box>
                
                <Stack spacing={3}>
                  {/* AI Model Selection */}
                  <Card variant="outlined" sx={{ mb: 2 }}>
                    <CardContent sx={{ p: 3, '&:last-child': { pb: 3 } }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <AutoAwesomeIcon sx={{ color: 'primary.main', mr: 1 }} />
                        <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                          AI Model Selection
                        </Typography>
                      </Box>
                      <FormControl fullWidth variant="outlined" size="small" sx={{ mb: 2 }}>
                        <InputLabel id="model-label">Select AI model for processing</InputLabel>
                        <Select
                          labelId="model-label"
                          id="model-select"
                          value="advanced"
                          label="Select AI model for processing"
                        >
                          <MenuItem value="basic">Basic Model</MenuItem>
                          <MenuItem value="standard">Standard Model</MenuItem>
                          <MenuItem value="advanced">
                            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>Advanced Model</Typography>
                                <Chip
                                  icon={<RecommendIcon fontSize="small" />}
                                  label="Recommended"
                                  size="small"
                                  color="secondary"
                                  sx={{ 
                                    height: 22, 
                                    '& .MuiChip-label': { px: 1, fontSize: '0.7rem', fontWeight: 500 },
                                    '& .MuiChip-icon': { ml: 0.5, fontSize: '0.9rem' }
                                  }}
                                />
                              </Box>
                              <Typography variant="caption" color="text.secondary">
                                Most accurate classification and anomaly detection
                              </Typography>
                            </Box>
                          </MenuItem>
                        </Select>
                      </FormControl>
                      <Typography variant="body2" color="text.secondary">
                        The advanced model offers superior categorization accuracy and spend pattern recognition
                      </Typography>
                    </CardContent>
                  </Card>
                  
                  {/* Data Enrichment */}
                  <Card variant="outlined" sx={{ mb: 2 }}>
                    <CardContent sx={{ p: 3, '&:last-child': { pb: 3 } }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <CelebrationIcon sx={{ color: 'primary.main', mr: 1 }} />
                        <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                          Data Enrichment Features
                        </Typography>
                      </Box>
                      <Box sx={{ pl: 1, mb: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <Chip 
                            label="ENABLED" 
                            size="small" 
                            color="success"
                            sx={{ mr: 2, fontWeight: 500 }}
                          />
                          <Typography variant="body2">
                            Supplier Risk Analysis
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <Chip 
                            label="ENABLED" 
                            size="small" 
                            color="success"
                            sx={{ mr: 2, fontWeight: 500 }}
                          />
                          <Typography variant="body2">
                            Spend Category Prediction
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <Chip 
                            label="ENABLED" 
                            size="small" 
                            color="success"
                            sx={{ mr: 2, fontWeight: 500 }}
                          />
                          <Typography variant="body2">
                            Price Benchmarking
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Chip 
                            label="ENABLED" 
                            size="small" 
                            color="success"
                            sx={{ mr: 2, fontWeight: 500 }}
                          />
                          <Typography variant="body2">
                            Sustainability Impact
                          </Typography>
                        </Box>
                      </Box>
                      <Alert severity="info" sx={{ mb: 1 }}>
                        <Typography variant="body2">
                          All data enrichment features are enabled with your current plan
                        </Typography>
                      </Alert>
                    </CardContent>
                  </Card>
                </Stack>
              </Box>
              
              {/* Processing Summary */}
              <Box sx={{ mt: 4, mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <InfoIcon sx={{ color: 'primary.main', mr: 1 }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                    Processing Summary
                  </Typography>
                </Box>
                
                <Alert severity="success" sx={{ mb: 3 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    Your data is ready for processing with the following configuration:
                  </Typography>
                  <Box sx={{ mt: 1 }}>
                    {fileData.data && (
                      <>
                        <Typography variant="body2">• {fileData.data.length} records will be processed</Typography>
                        <Typography variant="body2">• Advanced AI model will be used for classification</Typography>
                        <Typography variant="body2">• All data enrichment features are enabled</Typography>
                        <Typography variant="body2">• Estimated processing time: {fileData.data.length > 500 ? '3-5' : '1-2'} minutes</Typography>
                      </>
                    )}
                    {!fileData.data && (
                      <>
                        <Typography variant="body2">• 0 records will be processed</Typography>
                        <Typography variant="body2">• Advanced AI model will be used for classification</Typography>
                        <Typography variant="body2">• All data enrichment features are enabled</Typography>
                        <Typography variant="body2">• Estimated processing time: 1-2 minutes</Typography>
                      </>
                    )}
                  </Box>
                </Alert>
                
                <Typography variant="body2" color="text.secondary">
                  Click "Start Analysis" to begin processing your data and generating insights
                </Typography>
              </Box>
            </Box>
          ) : (
            <div>Unexpected step</div>
          )}
        </CardContent>
      </Card>
      
      {/* Processing Modal */}
      {showProcessingModal && (
        <Box 
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 9999,
          }}
        >
          <Card 
            sx={{
              maxWidth: 500,
              width: '90%',
              p: 4,
              textAlign: 'center',
              borderRadius: 3
            }}
          >
            <Box 
              sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center',
                gap: 3
              }}
            >
              <Typography variant="h5" component="h2" fontWeight={600}>
                Processing Data
              </Typography>
              
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: 120,
                  width: 120,
                  position: 'relative',
                  animation: 'spin 2s linear infinite',
                  '@keyframes spin': {
                    '0%': {
                      transform: 'rotate(0deg)',
                    },
                    '100%': {
                      transform: 'rotate(360deg)',
                    },
                  },
                }}
              >
                <AutoAwesomeIcon 
                  sx={{ 
                    fontSize: 80, 
                    color: 'primary.main',
                  }} 
                />
              </Box>
              
              <Typography variant="body1" paragraph>
                {fileData.data && fileData.data.length > 0 
                  ? `Processing ${fileData.data.length} records...` 
                  : 'Processing your data...'}
              </Typography>
              
              <Box sx={{ width: '100%', mt: 2 }}>
                <Typography variant="body2" color="text.secondary" mb={1} textAlign="left">
                  Processing steps:
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Alert severity="success" sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography variant="body2">Analyzing supplier data</Typography>
                  </Alert>
                  <Alert severity="success" sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography variant="body2">Categorizing spend items</Typography>
                  </Alert>
                  <Alert severity="info" sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography variant="body2">Generating insights</Typography>
                  </Alert>
                  <Alert severity="info" sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography variant="body2">Preparing visualizations</Typography>
                  </Alert>
                </Box>
              </Box>
              
              <Typography variant="body2" color="text.secondary" mt={2}>
                This may take a few moments. Please don't close this window.
              </Typography>
            </Box>
          </Card>
        </Box>
      )}
    </Box>
  );
};

export default FileUploadPage; 